import type {
  EmploymentTemplate,
  EmploymentTemplateSalary,
} from "services/api/company/staff.service";

const AGE_SALARY_REQUIREMENT_TYPE = "Age";
const EXPERIENCE_SALARY_REQUIREMENT_TYPE = "Experience";

/**
 * Returns the template value unless it is unset (null/empty/"Undefined"), in
 * which case the fallback is used.
 */
export const toTemplateEnumOrFallback = <TFallback extends string | undefined>(
  value: string | null,
  fallback: TFallback,
): string | TFallback => {
  if (value == null || value.length === 0 || value === "Undefined") {
    return fallback;
  }

  return value;
};

/**
 * Converts a template's numeric salary value to the form's string representation,
 * falling back to the current value when the template has none.
 */
export const toTemplateSalaryString = (
  salaryValue: number | null,
  fallback: string,
): string => (salaryValue == null ? fallback : String(salaryValue));

/**
 * Maps the employment-template fields shared by both staff forms (everything
 * except the salary amount, whose key differs between the forms) onto the given
 * fallback values (the current form values). Each caller applies the salary
 * field itself with `toTemplateSalaryString`.
 */
export const toEmploymentTemplateSharedFields = <
  TEmploymentType extends string | undefined,
  TSalaryType extends string | undefined,
>(
  template: EmploymentTemplate,
  fallback: {
    employmentType: TEmploymentType;
    salaryType: TSalaryType;
    employeeCategory: string;
    paymentType: string;
    role: string;
    workPercentage: string;
    agreement: string;
    noticeDays: string;
    vacationDays: string;
    workplace: string;
  },
): {
  employmentType: string | TEmploymentType;
  salaryType: string | TSalaryType;
  employeeCategory: string;
  paymentType: string;
  role: string;
  workPercentage: string;
  agreement: string;
  noticeDays: string;
  vacationDays: string;
  workplace: string;
} => ({
  employmentType: toTemplateEnumOrFallback(
    template.employmentType,
    fallback.employmentType,
  ),
  salaryType: toTemplateEnumOrFallback(
    template.salaryType,
    fallback.salaryType,
  ),
  employeeCategory: toTemplateEnumOrFallback(
    template.employeeCategory,
    fallback.employeeCategory,
  ),
  paymentType: toTemplateEnumOrFallback(
    template.paymentType,
    fallback.paymentType,
  ),
  role: template.role ?? fallback.role,
  workPercentage:
    template.workPercentage == null
      ? fallback.workPercentage
      : String(Math.round(template.workPercentage * 100)),
  agreement: template.agreement ?? fallback.agreement,
  noticeDays:
    template.noticeDays == null
      ? fallback.noticeDays
      : String(template.noticeDays),
  vacationDays:
    template.vacationDays == null
      ? fallback.vacationDays
      : String(template.vacationDays),
  workplace: template.workplace ?? fallback.workplace,
});

/**
 * Filters template salaries by the worker's age:
 * - `Age` salaries are kept when the worker's age is at or below the
 *   requirement (age brackets are upper bounds, e.g. a youth wage).
 * - When the worker matches at least one `Age` requirement, `Experience`
 *   salaries are hidden (age brackets are low and experience requirements high,
 *   so they don't apply to someone selected by a youth age bracket).
 * - Salaries with no age/experience requirement always pass.
 * - When the worker's age is unknown, all salaries are kept.
 */
export const filterTemplateSalariesByAge = (
  salaries: EmploymentTemplateSalary[],
  workerAge: number | null,
): EmploymentTemplateSalary[] => {
  if (workerAge == null) return salaries;

  const matchesAnyAgeRequirement = salaries.some(
    (salary) =>
      salary.requirementType === AGE_SALARY_REQUIREMENT_TYPE &&
      workerAge <= salary.requirement,
  );

  return salaries.filter((salary) => {
    if (salary.requirementType === AGE_SALARY_REQUIREMENT_TYPE) {
      return workerAge <= salary.requirement;
    }
    if (salary.requirementType === EXPERIENCE_SALARY_REQUIREMENT_TYPE) {
      return !matchesAnyAgeRequirement;
    }
    return true;
  });
};
